import { useContext, useMemo } from 'react';
import FlexCentered from '../../../../Common/FlexCentered';
import Typography from '../../../../../kingpin/atoms/Typography';
import CurrentPayCard from './CurrentPayCard';
import useValueFormatters from '../../../../../hooks/useValueFormatters';
import Loading from '../../../../Loading';
import Card from '../../../../../kingpin/atoms/Card';
import { BonusOverviewContext } from '../../../../DriverAX/providers/BonusOverviewProvider';
import RewardBandsTooltip from '../../../../DriverAX/components/RewardBandsTooltip';
import { getRewards } from '../../../../DriverAX/utils/rewardBandsUtils';

const BonusTotalReward = ({
  bonusOverview,
}: {
  bonusOverview: BonusTypes.BonusOverview;
}) => {
  const { formatValue } = useValueFormatters();
  const isScoringProgram = !!bonusOverview.bonusProgram.isScoringProgram;

  const current = useMemo(() => {
    if (isScoringProgram) {
      return `${formatValue({ value: bonusOverview.rewardOutput.totalReward, formatting: { precision: 0 } })} PTS`;
    }
    return formatValue({
      value: bonusOverview.rewardOutput.totalReward,
      formatting: { prefix: 'currency', precision: 2 },
    });
  }, [formatValue, bonusOverview.rewardOutput.totalReward, isScoringProgram]);

  const available = useMemo(() => {
    if (isScoringProgram) {
      return `${formatValue({ value: bonusOverview.rewardOutput.potentialTotalReward, formatting: { precision: 0 } })} PTS`;
    }
    return formatValue({
      value: bonusOverview.rewardOutput.potentialTotalReward,
      formatting: { prefix: 'currency', precision: 2 },
    });
  }, [
    formatValue,
    bonusOverview.rewardOutput.potentialTotalReward,
    isScoringProgram,
  ]);

  const milesToPayConversion = useMemo(():
    | BonusTypes.Rewards.AnyRewardOutput
    | undefined => {
    return getRewards(bonusOverview.rewardOutput).find(
      (r) => r.name === 'milesToPayConversion',
    );
  }, [bonusOverview.rewardOutput]);

  const TooltipContent = useMemo(() => {
    if (bonusOverview.bonusProgram.rewardType !== 'points') {
      return undefined;
    }

    if (!milesToPayConversion) {
      return undefined;
    }

    if (!milesToPayConversion.rewardBands) {
      return undefined;
    }

    return (
      <RewardBandsTooltip rewardBands={milesToPayConversion.rewardBands} />
    );
  }, [bonusOverview.bonusProgram.rewardType, milesToPayConversion]);

  return (
    <>
      <CurrentPayCard
        label={isScoringProgram ? 'Total Points' : 'Current Bonus Pay'}
        current={current}
        available={available}
        TooltipContent={TooltipContent}
      />
    </>
  );
};

const Gate = () => {
  const { isLoading, bonusOverview } = useContext(BonusOverviewContext);

  if (isLoading) {
    return (
      <Card style={{ height: 150 }}>
        <Loading />
      </Card>
    );
  }

  if (!bonusOverview) {
    return (
      <FlexCentered style={{ height: 150 }}>
        <Typography.Header type={'H4'}>
          Bonus information not found
        </Typography.Header>
      </FlexCentered>
    );
  }

  return <BonusTotalReward bonusOverview={bonusOverview} />;
};

export default Gate;
